/**
 * @file `<MentorDock/>` — the mentor surface docked to the terminal pane (#939).
 * @module @labcraft/course-kit/components/mentor-dock
 *
 * Build Spec §6, reshaped by #1566 into a **per-objective transcript**: a
 * quiet bar ("AI mentor · watching · N commands") that expands into the
 * active objective's chronological mentor history — authored `hook`s
 * (trusted HTML, the curated wrong-turn line), AI `nudge`s (plain text,
 * rung-labelled), and the success-after-struggle `encouragement` capstone
 * (plain text) — oldest→newest. A live nudge no longer clobbers the authored
 * hook: both are entries in the same list (folds #1569). Below the entries an
 * honest-pending line reflects whether the AI is standing by (an authored
 * hook covers it), looking (a model call is coming), or quiet.
 *
 * When the active objective has no history the dock shows the completed
 * objective's transient `capstone` (if one is holding), else the quiet
 * prompt.
 *
 * XSS discipline: nudge + encouragement text is model output and renders as
 * **plain text** — never through `dangerouslySetInnerHTML`. The authored
 * `hook` html keeps its innerHTML path (course content, same trust domain as
 * the chapter MDX).
 *
 * Reads {@link useMentor}, so it must render inside `<ChapterProvider/>`.
 * The platform docks it at the bottom of the terminal pane.
 */

import type { JSX } from 'react'
import type { TranscriptEntry } from '../runtime/reducer.ts'
import { useMentor } from '../runtime/context.tsx'
import styles from './mentor_dock.module.css'

/**
 * Restraint-ladder labels for a nudge entry's rung (#1148): 1 = nudge
 * (orienting question), 2 = reframe (point at the concept), 3 = shape
 * (structure of the approach). Unknown rungs fall back to the rung-1 label
 * so a future ladder extension degrades gracefully.
 */
const RUNG_LABELS: Record<number, string> = {
  1: 'AI mentor · nudge',
  2: 'AI mentor · reframe',
  3: 'AI mentor · shape',
}

/** Soft cap on rendered transcript entries — the rest scroll off the top. */
const MAX_RENDERED = 12

/**
 * Render one transcript entry by `kind` — the XSS split lives here: a `hook`
 * is authored HTML (trusted innerHTML); `nudge` / `encouragement` are model
 * output and render as escaped plain text.
 *
 * @param entry - the transcript entry to render
 * @param key - the React list key
 * @returns the rendered entry
 */
function renderEntry(entry: TranscriptEntry, key: number): JSX.Element {
  if (entry.kind === 'hook') {
    return (
      <div className={styles.msg} role="status" aria-live="polite" key={key}>
        <span className={styles.msgLabel}>Heads up</span>
        <div
          className={styles.msgText}
          dangerouslySetInnerHTML={{ __html: entry.html }}
        />
      </div>
    )
  }
  const label =
    entry.kind === 'encouragement'
      ? 'AI mentor · nice work'
      : (RUNG_LABELS[entry.rung] ?? RUNG_LABELS[1])
  return (
    <div className={styles.msg} role="status" aria-live="polite" key={key}>
      <span className={styles.msgLabel}>{label}</span>
      <div className={styles.msgText}>{entry.text}</div>
    </div>
  )
}

export function MentorDock(): JSX.Element {
  const { commandCount, transcript, capstone, pending } = useMentor()

  // The active transcript takes precedence; when the active objective has no
  // history, a holding capstone (the just-completed objective's win) shows.
  const entries: TranscriptEntry[] =
    transcript.length > 0 ? transcript : (capstone?.entries ?? [])
  const rendered = entries.slice(-MAX_RENDERED)

  const pendingLine =
    pending === 'looking'
      ? 'AI looking…'
      : pending === 'standing-by'
        ? 'AI standing by'
        : null

  return (
    <div className={styles.dock}>
      <div className={styles.bar}>
        <span className={styles.orb} aria-hidden />
        <span className={styles.who}>AI mentor</span>
        <span className={styles.watch}>
          <span className={styles.livedot} aria-hidden />
          watching · {commandCount} command{commandCount === 1 ? '' : 's'}
        </span>
      </div>
      <div className={styles.body}>
        {rendered.length > 0 ? (
          <>
            <div className={styles.transcript}>
              {rendered.map((entry, i) => renderEntry(entry, i))}
            </div>
            {pendingLine ? (
              <p className={styles.pending} role="status" aria-live="polite">
                {pendingLine}
              </p>
            ) : null}
          </>
        ) : pendingLine ? (
          <p className={styles.pending} role="status" aria-live="polite">
            {pendingLine}
          </p>
        ) : (
          <p className={styles.quiet}>
            Quiet — type away. The AI mentor flags a known wrong turn if you hit
            one.
          </p>
        )}
      </div>
    </div>
  )
}
